import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import ProjectCards from "../../utils/ProjectCards";
import {
    fetchActiveCategories,
    fetchTopVideos,
} from "../../services/firestore";
import { resolveLocalizedField, resolveByLangSuffix, getCategorySlug } from "../../utils/i18nHelpers";
import { useTranslation } from "react-i18next";
import { gsap, ScrollTrigger } from "../../utils/gsapSetup";

const Projects = () => {
    const [allVideos, setAllVideos] = useState([]);
    const [visibleVideos, setVisibleVideos] = useState([]);
    const sectionRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [rawCategories, setRawCategories] = useState([]);
    const [rawTopVideos, setRawTopVideos] = useState([]);
    const { i18n } = useTranslation();

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                // console.log("Firestore verileri yükleniyor...");

                const [categories, topVideos] = await Promise.all([
                    fetchActiveCategories(),
                    fetchTopVideos(),
                ]);

                // console.log("Kategoriler:", categories);
                // console.log("Top videolar:", topVideos);

                setRawCategories(categories);
                setRawTopVideos(topVideos);
            } catch (error) {
                console.error("Firestore verileri alınırken hata:", error);
                console.error("Hata detayı:", error.message);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    // Dil değiştiğinde veya ham veriler geldiğinde birleşik listeyi yeniden hesapla
    useEffect(() => {
        if (rawCategories.length === 0 || rawTopVideos.length === 0) return;
        const merged = rawCategories
            .map((cat) => {
                const video = rawTopVideos.find((v) => v.category === cat.id || v.category_id === cat.id || v.category?.id === cat.id);
                if (!video) {
                    return null;
                }
                return {
                    categoryId: cat.id,
                    categorySlug: getCategorySlug(cat, "tr"),
                    categoryTitle:
                        resolveByLangSuffix(cat, "name", "") ||
                        resolveLocalizedField(cat?.title) ||
                        cat?.name_tr ||
                        "",
                    order_num: cat.order_num ?? 0,
                    title: resolveLocalizedField(video?.title),
                    desc: resolveLocalizedField(video?.desc),
                    videoURL: video?.videoURL || video?.video_url || "",
                    videoURLWeb: video?.video_url_web || "",
                    thumbnailUrl: video?.thumbnail_url || "",
                    order: video?.order_num ?? 1,
                };
            })
            .filter(Boolean)
            .sort((a, b) => (b.order_num || 0) - (a.order_num || 0));
        setAllVideos(merged);
        setVisibleVideos(new Array(merged.length).fill(false));
    }, [rawCategories, rawTopVideos, i18n.language]);

    const filteredVideos = useMemo(() => allVideos.filter(video => video.categorySlug !== "web-hizmetleri"), [allVideos]);

    useEffect(() => {
        if (!sectionRef.current || filteredVideos.length === 0) return;

        const triggers = [];
        const cards = sectionRef.current.querySelectorAll(".project-card");
        cards.forEach((card) => {
            const anim = gsap.fromTo(
                card,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: card,
                        start: "top 80%",
                        toggleActions: "play none none reverse",
                    },
                    onStart: () => {
                        const idx = card.getAttribute("data-index");
                        setVisibleVideos((prev) =>
                            prev.map((visible, i) => (i === parseInt(idx) ? true : visible))
                        );
                    },
                }
            );
            if (anim.scrollTrigger) triggers.push(anim.scrollTrigger);
        });
        ScrollTrigger.refresh();

        return () => {
            triggers.forEach((st) => st.kill());
        };
    }, [filteredVideos]);

    if (loading) {
        return (
            <section className="projects w-full min-h-[60vh] flex items-center justify-center bg-black">
                <div className="loader"></div>
            </section>
        );
    }

    return (
        <motion.section
            className="projects"
            initial="offscreen"
            whileInView="onscreen"
            ref={sectionRef}
        >
            {filteredVideos.map((card, index) => {
                let cls = "right";
                if (index % 2 === 0) {
                    cls = "left";
                }

                return (
                    <div
                        key={index}
                        className={`project-card ${cls}`}
                        data-index={index} // Her bir karta unique index atadık
                    >
                        <ProjectCards
                            class_spec={cls}
                            title={card.title}
                            order={card.order}
                            categoryTitle={card.categoryTitle}
                            categoryId={card.categoryId}
                            categorySlug={card.categorySlug} // Slug'ı da geçir
                            desc={card.desc}
                            videoURL={
                                visibleVideos[index] ? card.videoURL : null
                            } // Video görünürse yüklenir
                            videoURLWeb={
                                visibleVideos[index] ? card.videoURLWeb : null
                            }
                            thumbnailUrl={card.thumbnailUrl}
                        />
                    </div>
                );
            })}
        </motion.section>
    );
};

export default Projects;
